import type {
    CreditPackageDto,
    CreditSubscriptionTierDto,
    MobileReleaseInfoDto,
    PlanDto,
    PlatformStatsDto,
    PremiumAddOnCatalogItemDto,
    ProductKitDto,
    SiteConfigDto,
} from '@tracedocuments/types';
import { getApiBaseUrl } from './fetch';
import { createRequestSignal } from './request-timeout';

const PUBLIC_READ_TIMEOUT_MS = 15_000;

async function fetchPublic(path: string, init?: RequestInit): Promise<Response> {
    const request = createRequestSignal(init?.signal, PUBLIC_READ_TIMEOUT_MS);
    try {
        return await fetch(`${getApiBaseUrl()}${path}`, { ...init, signal: request.signal });
    } finally {
        request.dispose();
    }
}

export async function fetchPlans(): Promise<PlanDto[]> {
    try {
        const res = await fetchPublic('/api/billing/plans', {
            cache: 'no-store',
        });
        if (!res.ok) return [];
        return await res.json();
    } catch {
        return [];
    }
}

export async function fetchPlatformStats(): Promise<PlatformStatsDto | null> {
    try {
        const res = await fetchPublic('/api/stats/public', {
            next: { revalidate: 900 },
        } as RequestInit);
        if (!res.ok) return null;
        return await res.json();
    } catch {
        return null;
    }
}

export async function fetchCreditPackages(): Promise<CreditPackageDto[]> {
    try {
        const res = await fetchPublic('/api/billing/credit-packages', {
            next: { revalidate: 300 },
        } as RequestInit);
        if (!res.ok) return [];
        return await res.json();
    } catch {
        return [];
    }
}

export async function fetchSiteConfig(): Promise<SiteConfigDto | null> {
    try {
        const res = await fetchPublic('/api/public/site-config', {
            next: { revalidate: 900 },
        } as RequestInit);
        if (!res.ok) return null;
        return await res.json();
    } catch {
        return null;
    }
}

export async function fetchMobileReleaseInfo(
    platform: 'android' | 'ios',
): Promise<MobileReleaseInfoDto | null> {
    try {
        const res = await fetchPublic(`/api/public/mobile-release-info?platform=${platform}`);
        if (!res.ok) return null;
        return await res.json();
    } catch {
        return null;
    }
}

export async function fetchCreditSubscriptionTiers(): Promise<CreditSubscriptionTierDto[]> {
    try {
        const res = await fetchPublic('/api/billing/credit-subscriptions', {
            next: { revalidate: 300 },
        } as RequestInit);
        if (!res.ok) return [];
        return await res.json();
    } catch {
        return [];
    }
}

export async function fetchPremiumAddOnsCatalog(): Promise<PremiumAddOnCatalogItemDto[]> {
    try {
        const res = await fetchPublic('/api/billing/premium-addons/catalog', {
            next: { revalidate: 300 },
        } as RequestInit);
        if (!res.ok) return [];
        return await res.json();
    } catch {
        return [];
    }
}

export async function fetchProductKit(code: string): Promise<ProductKitDto | null> {
    try {
        const res = await fetchPublic(`/api/billing/product-kits/${encodeURIComponent(code)}`, {
            next: { revalidate: 300 },
        } as RequestInit);
        if (!res.ok) return null;
        return await res.json();
    } catch {
        return null;
    }
}

// Re-export types
export type {
    CreditPackageDto,
    CreditSubscriptionTierDto, PlanDto, PlanFeatureDto, PlatformStatsDto,
    PremiumAddOnCatalogItemDto, ProductKitDto, SiteConfigDto,
    MobileReleaseInfoDto,
} from '@tracedocuments/types';

